import { useState, useRef, useEffect } from 'react';
import { Button, ButtonGroup } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface ImageCropProps {
  imageUrl: string;
  onConfirm: (images: Blob[]) => void;
  onCancel: () => void;
  onPrevious?: () => void;
  // 以下兩個 prop 皆為 optional，未提供時行為與原本完全一致（供標注工具使用，kanban 108125）
  initialLines?: number[]; // 原圖像素座標；提供時作為 cropLines 初始值，取代預設均分
  onConfirmLines?: (lines: number[]) => void; // 提供時「確認分段」只回傳線座標，不裁切/不產生 Blob/不呼叫 onConfirm
}

// 將 initialLines 校驗並 clamp 進 [50, width-50]，同時維持遞增 + 最小 50px 間距；
// 不合法時回傳 null，呼叫端應退回均分並記錄 console.warn
const clampInitialLines = (lines: number[], width: number): number[] | null => {
  if (!lines || lines.length === 0) return null;
  if (width <= 100) return null; // 沒有足夠空間容納 [50, width-50] 區間

  const clamped = lines.map((l) => Math.min(Math.max(l, 50), width - 50));

  for (let i = 0; i < clamped.length; i++) {
    if (!Number.isFinite(clamped[i])) return null;
    if (i > 0 && clamped[i] - clamped[i - 1] < 50) return null;
  }

  return clamped;
};

export const PerspectiveCorrectionWithCrop: React.FC<ImageCropProps> = ({
  imageUrl,
  onConfirm,
  onCancel,
  onPrevious,
  initialLines,
  onConfirmLines,
}) => {
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  // 分割相關狀態
  const [cropDivisions, setCropDivisions] = useState(() =>
    initialLines && initialLines.length > 0 ? initialLines.length + 1 : 3
  );
  const [cropLines, setCropLines] = useState<number[]>([]);
  const [draggedLine, setDraggedLine] = useState<number | null>(null);
  const initialLinesAppliedRef = useRef(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 初始化圖片
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // 當分割數量改變時，重新計算分割線位置
  useEffect(() => {
    if (imageSize.width > 0) {
      // 只在初次取得圖片尺寸時套用 initialLines（若提供且合法）；
      // 之後使用者用 +/- 改 cropDivisions 一律回到均分邏輯
      if (!initialLinesAppliedRef.current && initialLines && initialLines.length > 0) {
        initialLinesAppliedRef.current = true;
        const validated = clampInitialLines(initialLines, imageSize.width);
        if (validated) {
          setCropLines(validated);
          return;
        }
        console.warn(
          '[PerspectiveCorrectionWithCrop] initialLines 不合法，退回均分',
          initialLines,
          imageSize.width
        );
      }

      const lines: number[] = [];
      const width = imageSize.width;

      for (let i = 1; i < cropDivisions; i++) {
        lines.push((width * i) / cropDivisions);
      }
      setCropLines(lines);
    }
  }, [cropDivisions, imageSize, initialLines]);

  // 繪製圖片和分割線
  useEffect(() => {
    if (!canvasRef.current || !imageSize.width) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      // 繪製圖片
      ctx.drawImage(img, 0, 0);
      
      // 繪製分割線
      if (cropLines.length > 0) {
        ctx.strokeStyle = '#ff6b35';
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        
        cropLines.forEach((lineX, index) => {
          // 繪製分割線
          ctx.strokeStyle = '#ff6b35';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(lineX, 0);
          ctx.lineTo(lineX, img.height);
          ctx.stroke();
          
          // 繪製拖拽把手
          ctx.fillStyle = '#ff6b35';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          const handleY = img.height / 2;
          ctx.beginPath();
          ctx.arc(lineX, handleY, 8, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          
          // 繪製分割序號（中文作文從右到左，所以數字要反向）
          ctx.fillStyle = '#ffffff';
          ctx.font = '14px Arial';
          ctx.textAlign = 'center';
          const chineseOrderNumber = cropLines.length - index;
          ctx.fillText(`${chineseOrderNumber}`, lineX, handleY + 4);
        });
      }
    };
    img.src = imageUrl;
  }, [imageUrl, cropLines, imageSize]);

  // 處理滑鼠和觸控事件
  const getMousePosition = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const getTouchPosition = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || e.touches.length === 0) return { x: 0, y: 0 };

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touch = e.touches[0];

    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const mousePos = getMousePosition(e);
    
    // 檢查是否點擊在分割線上
    for (let i = 0; i < cropLines.length; i++) {
      if (Math.abs(mousePos.x - cropLines[i]) < 15) {
        setDraggedLine(i);
        break;
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (draggedLine !== null && imageSize.width > 0) {
      const mousePos = getMousePosition(e);
      
      // 限制分割線的移動範圍
      const minX = draggedLine === 0 ? 50 : cropLines[draggedLine - 1] + 50;
      const maxX = draggedLine === cropLines.length - 1 ? imageSize.width - 50 : cropLines[draggedLine + 1] - 50;
      
      const newX = Math.max(minX, Math.min(maxX, mousePos.x));
      
      setCropLines(prev => {
        const newLines = [...prev];
        newLines[draggedLine] = newX;
        return newLines;
      });
    }
  };

  const handleMouseUp = () => {
    setDraggedLine(null);
  };

  // 觸控事件處理函數
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // 防止預設的觸控行為
    const touchPos = getTouchPosition(e);

    // 檢查是否點擊在分割線上
    for (let i = 0; i < cropLines.length; i++) {
      if (Math.abs(touchPos.x - cropLines[i]) < 20) { // 觸控區域稍微大一點
        setDraggedLine(i);
        break;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // 防止頁面滾動
    if (draggedLine !== null && imageSize.width > 0) {
      const touchPos = getTouchPosition(e);

      // 限制分割線的移動範圍
      const minX = draggedLine === 0 ? 50 : cropLines[draggedLine - 1] + 50;
      const maxX = draggedLine === cropLines.length - 1 ? imageSize.width - 50 : cropLines[draggedLine + 1] - 50;

      const newX = Math.max(minX, Math.min(maxX, touchPos.x));

      setCropLines(prev => {
        const newLines = [...prev];
        newLines[draggedLine] = newX;
        return newLines;
      });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setDraggedLine(null);
  };

  // 應用分割
  const applyProcessing = async () => {
    if (onConfirmLines) {
      onConfirmLines([...cropLines]);
      return;
    }

    if (imageSize.width === 0) return;

    setIsProcessing(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = async () => {
        const croppedImages: Blob[] = [];
        
        // 計算每個分割區域
        const sections: { x: number; width: number }[] = [];
        
        let startX = 0;
        for (const lineX of cropLines) {
          sections.push({ x: startX, width: lineX - startX });
          startX = lineX;
        }
        // 加入最後一個區域
        sections.push({ x: startX, width: img.width - startX });
        
        // 生成每個分割區域的圖片
        for (const section of sections) {
          canvas.width = section.width;
          canvas.height = img.height;
          
          // 清除畫布
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // 繪製分割後的圖片
          ctx.drawImage(
            img,
            section.x, 0, section.width, img.height,
            0, 0, section.width, img.height
          );
          
          // 轉換為 Blob
          const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((blob) => {
              resolve(blob!);
            }, 'image/jpeg', 0.9);
          });
          
          croppedImages.push(blob);
        }
        
        onConfirm(croppedImages);
      };
      
      img.src = imageUrl;
    } catch (error) {
      console.error('分割處理失敗:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 略過分段:整圖(透視校正後)以單一段輸出,不強制使用者切段(對齊國中「略過」鍵)
  const skipCropping = async () => {
    if (imageSize.width === 0) return;
    setIsProcessing(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const img = new Image();
      img.onload = async () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.9);
        });
        onConfirm([blob]);
      };
      img.src = imageUrl;
    } catch (error) {
      console.error('略過分段處理失敗:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
      {/* 背景遮罩 */}
      <div className="absolute inset-0 bg-black bg-opacity-75" />

      {/* 模態內容 */}
      <div className="relative w-[98vw] max-h-[95vh] lg:h-[88vh] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden" style={{ touchAction: 'none' }}>
        {/* 關閉按鈕 */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* 主要編輯區域 */}
        <div className="flex-1 overflow-auto p-3 sm:p-5 lg:p-7">
          <div className="h-full grid grid-cols-1 gap-3">
            {/* 編輯區域 */}
            <div className="flex flex-col h-full">
              <div className="mb-2 sm:mb-3 text-center">
                <h3 className="text-base sm:text-lg font-semibold text-gray-700">設定作文段落</h3>
              </div>

              <div className="flex-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-2 relative h-[55vh] lg:h-[400px] min-h-[280px]" style={{ touchAction: 'none' }}>
                <div className="absolute inset-2 flex items-center justify-center overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    className="max-w-full max-h-full object-contain"
                    style={{
                      cursor: draggedLine !== null ? 'grabbing' : 'grab',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      touchAction: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                </div>
              </div>

              {/* 控制按鈕區域 */}
              <div className="mt-3 p-2 sm:p-3 bg-gray-50 border border-gray-200 rounded-lg">
                {/* 操作提醒 */}
                <p className="text-xs sm:text-sm font-bold text-center mb-2 sm:mb-3" style={{ color: '#254D71' }}>
                  操作提醒：請將分隔線拖曳至文字間的空白處。若作文超過六段，分隔線不需精準切分對應段落，但請勿壓到文字，這能幫助 AI 辨識更精準。
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {/* 上一步按鈕 */}
                  {onPrevious && (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={onPrevious}
                      disabled={isProcessing}
                      size="medium"
                    >
                      上一步
                    </Button>
                  )}
                  
                  <ButtonGroup variant="outlined" size="medium">
                    {[1, 2, 3, 4, 5, 6].map((num) => (
                      <Button
                        key={num}
                        variant={cropDivisions === num ? "contained" : "outlined"}
                        onClick={() => setCropDivisions(num)}
                        disabled={isProcessing}
                      >
                        {num}段
                      </Button>
                    ))}
                  </ButtonGroup>
                  
                  <Button
                    variant="outlined"
                    onClick={skipCropping}
                    disabled={isProcessing}
                    size="medium"
                  >
                    略過(不分段)
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={applyProcessing}
                    disabled={isProcessing}
                    color="primary"
                    size="medium"
                  >
                    {isProcessing ? '處理中...' : '確認分段'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
